const LOADING_PAGE_HEIGHT_PREFIX = 'expected_loading_page_height_';

type StoredLoadingPageHeight = {
  height: number;
  windowWidth: number;
  windowHeight: number;
};

// Called from the referral form just before it navigates away (a full page
// nav, so sessionStorage is what survives the jump). Measures its own
// header/footer, assumed identical to the results page's, so the results
// page can size itself on first render — see LoadingAnimation. Must match
// its formula and selectors exactly, or the guess and the live
// recalculation disagree. Stores the viewport size alongside the height so
// LoadingAnimation can tell if it's stale (window resized, device rotated).
function storeExpectedLoadingPageHeight(rfid: string): void {
  try {
    const header = document.querySelector('header.rs-Masthead');
    // On mobile navPanel is the off-canvas menu drawer, hidden via
    // visibility:hidden rather than display:none — see LoadingAnimation's
    // matching comment. Only count its height while actually visible.
    const navPanel = document.getElementById('nav-primary-panel');
    const navPanelVisible =
      navPanel && getComputedStyle(navPanel).visibility !== 'hidden';
    const footer = document.querySelector('footer.rs-SiteFooter');
    if (!header && !footer) return;

    const height =
      window.innerHeight -
      (header?.getBoundingClientRect().height ?? 0) -
      (navPanelVisible ? navPanel.getBoundingClientRect().height : 0) -
      (footer?.getBoundingClientRect().height ?? 0);

    const payload: StoredLoadingPageHeight = {
      height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight
    };

    sessionStorage.setItem(
      `${LOADING_PAGE_HEIGHT_PREFIX}${rfid}`,
      JSON.stringify(payload)
    );
  } catch {
    // quota exceeded or unavailable in private browsing — fail silently
  }
}

function getExpectedLoadingPageHeight(
  rfid: string
): StoredLoadingPageHeight | null {
  try {
    const stored = sessionStorage.getItem(
      `${LOADING_PAGE_HEIGHT_PREFIX}${rfid}`
    );
    if (stored === null) return null;
    return JSON.parse(stored) as StoredLoadingPageHeight;
  } catch {
    return null;
  }
}

// One-shot value — clear it once read so it doesn't linger in sessionStorage.
function clearExpectedLoadingPageHeight(rfid: string): void {
  try {
    sessionStorage.removeItem(`${LOADING_PAGE_HEIGHT_PREFIX}${rfid}`);
  } catch {
    // ignore
  }
}

export {
  storeExpectedLoadingPageHeight,
  getExpectedLoadingPageHeight,
  clearExpectedLoadingPageHeight
};
export type { StoredLoadingPageHeight };
